import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box, Button, Chip, IconButton, Link, Skeleton, Tooltip, Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import HomeWorkOutlinedIcon from '@mui/icons-material/HomeWorkOutlined';
import LandscapeOutlinedIcon from '@mui/icons-material/LandscapeOutlined';
import AddIcon from '@mui/icons-material/Add';
import { listCardActionIconSx } from '../../Map/components/UserLotListsPanel/UserLotListsPanel.styles';
import { recordCardHeaderIconSx } from '../../Map/components/RecordLotIdentity';
import {
  emptyStateBoxSx, emptyStateHintSx, emptyStateTitleSx,
} from '../../Map/components/UserLotListsPanel/UserLotListsPanel.styles';
import { leadsSummaryClearButtonSx } from '../../Leads/components/leadsUi.styles';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import RefreshIcon from '@mui/icons-material/Refresh';
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined';
import InfoNotice from '../../Components/InfoNotice';
import PanelEmptyState from '../../Components/PanelEmptyState';
import { confirmDialog } from '../../Components/ConfirmDialog';
import { formatMetaMoney } from '../metaAdsMoney';
import { META_DEFAULT_PERIOD_PRESET, metaPeriodIsPartial } from '../metaPeriod';
import { formatMetaDataUntil } from '../metaAdsConnection';
import { MetaAdsManagerGrid, MetaAdsManagerLine } from './MetaAdsManagerStats';
import { META_NO_DATA_IN_PERIOD, metaActivityValues, metaPeriodIsEmpty } from '../metaAdsManager';
import MetaContactsTooltip from './MetaContactsTooltip';
import { formatMetaContacts, metaContactsNote } from '../metaContacts';
import MetaContactsHelp from './MetaContactsHelp';
import MetaCampaignDetail, { CampaignReportButton, metaCampaignResultsGroup } from './MetaCampaignDetail';
import MetaCampaignPicker from './MetaCampaignPicker';
import {
  campaignLinkedRecords,
  describeMetaLinkedBy,
  describeMetaRecord,
  metaAdLinkLabel,
  metaCampaignLinkLabel,
  metaCampaignUnitsPlacement,
  metaRecordKey,
  metaRecordOpenItem,
} from '../metaCampaigns';
import useLeadItemOpener from '../../Leads/components/useLeadItemOpener';
import MetaLinkUnitsDialog from './MetaLinkUnitsDialog';
import MetaAdPreviewDialog from './MetaAdPreviewDialog';
import MetaAdThumb from './MetaAdThumb';
import MetaWriteDialog from './MetaWriteDialog';
import { LinkMenu, metaWriteItems } from './MetaActionMenu';
import { metaWriteBudgetOf } from '../metaWrite';
import { MetaActiveDot, MetaDeliveryPill, isMetaDeliveryActive, metaDeliveryShowsStatus } from './MetaDeliveryBits';
// Recomendaciones de Meta ocultas por decisión de producto (28/10/2026): se harán desde Terrio más adelante. Descomentar para reactivar.
// import { MetaRecommendations } from './MetaDeliveryBits';
// import { collectMetaRecommendations } from '../metaDelivery';
import {
  getMetaAdsStatusPillSx,
  getMetaAdsLevelTagSx,
  metaAdsAdBlockSx,
  metaAdsAdListSx,
  metaAdsAdMetricsTextSx,
  metaAdsAdNameTextSx,
  metaAdsCardSx,
  metaAdsContainedDisabledSx,
  metaAdsMutedTextSx,
  metaAdsSectionTitleSx,
  metaAdsSkeletonSx,
  metaAdsSkeletonMutedSx,
  metaCampaignsSpendSx,
  metaCampaignsSpendTextSx,
  metaCampaignsSpendLabelSx,
  metaCampaignsSpendAmountSx,
  metaAdsCompactHeadSx,
  metaCampaignHeadChipsSx,
  metaCampaignHeadContainerSx,
  metaCampaignHeadLineSx,
  metaCampaignStickyHeadSx,
  META_STICKY_HEAD_AIR,
  metaLinkedRecordChipSx,
  META_LINKED_UNIT_ICON,
  metaLinkedUnitAvatarSx,
  metaLinkedUnitNameButtonSx,
  metaLinkedUnitNameSx,
  metaLinkedUnitRemoveSx,
  metaLinkedUnitRowSx,
  metaLinkedUnitTagSx,
  metaLinkedUnitsListSx,
  metaLinkedUnitsTitleSx,
  metaLinkedRecordChipIconSx,
  metaAdsLineBoxSx,
  metaAdsTagInLineSx,
  metaCampaignsHeaderSx,
  metaCampaignsLayoutSx,
  metaCampaignsListSx,
} from './metaAdsUi.styles';
import MetaPeriodSelect from './MetaPeriodSelect';
import { getScrollEdgeFadeSx, useScrollEdgeFade } from '../../hooks/useScrollEdgeFade';

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;
// Con la campaña elegida (cabecera sticky) el fade de la lista es de 8 px (= el aire de arriba
// de la cabecera pegada): el fade cae sobre ese hueco y nunca sobre la cabecera.
const META_STICKY_FADE_SIZE = 8;

const KIND_TAGS = { user_lot: 'Unidad', cadastral_lot: 'Lote', cad_parcel: 'Lote' };

/**
 * Una unidad vinculada a la campaña como registro (t994u), con la forma de las filas de «Personas»
 * del modal de Compartir: avatar verde con el ícono de unidad / lote, nombre (con ficha resoluble,
 * botón que la abre: mismo `onOpen` que el chip), etiqueta gris a la derecha («Unidad» / «Lote»,
 * + «N anuncios» si la campaña tiene varios) y ✕ «Desvincular» (solo con `onDelete`, mismos
 * permisos y confirmación que el chip).
 */
function LinkedUnitRow({ rec, adsTotal = 0, onOpen = null, opening = false, onDelete, deleteLabel, testId }) {
  const canOpen = Boolean(onOpen && metaRecordOpenItem(rec));
  const kind = KIND_TAGS[rec?.itemKind] || 'Registro';
  const name = rec?.label || describeMetaRecord(rec);
  const Icon = rec?.itemKind === 'user_lot' ? HomeWorkOutlinedIcon : LandscapeOutlinedIcon;
  const tag = adsTotal > 1 && rec?.adsCount ? `${kind} · ${plural(rec.adsCount, 'anuncio', 'anuncios')}` : kind;
  return (
    <Box component="li" sx={metaLinkedUnitRowSx} data-testid={testId}>
      <Box sx={metaLinkedUnitAvatarSx} aria-hidden data-testid={testId ? `${testId}-avatar` : undefined}>
        <Icon sx={{ fontSize: META_LINKED_UNIT_ICON }} />
      </Box>
      {canOpen ? (
        <Tooltip title={<span>Abrir ficha</span>} placement="top" arrow describeChild>
          <Box
            component="button"
            type="button"
            sx={metaLinkedUnitNameButtonSx}
            aria-label={`Abrir ficha de ${describeMetaRecord(rec)}`}
            aria-busy={opening || undefined}
            onClick={() => { if (!opening) onOpen(rec); }}
            data-testid={testId ? `${testId}-open` : undefined}
          >
            {name}
          </Box>
        </Tooltip>
      ) : (
        <Typography sx={metaLinkedUnitNameSx} data-testid={testId ? `${testId}-name` : undefined}>{name}</Typography>
      )}
      <Typography component="span" sx={metaLinkedUnitTagSx} data-testid={testId ? `${testId}-tag` : undefined}>{tag}</Typography>
      {onDelete ? (
        <Tooltip title={<span>Desvincular</span>} placement="top" arrow>
          <IconButton size="small" aria-label={deleteLabel} onClick={onDelete} sx={metaLinkedUnitRemoveSx} data-testid={testId ? `${testId}-unlink` : undefined}>
            <CloseIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      ) : null}
    </Box>
  );
}

const textButtonSx = { textTransform: 'none', fontWeight: 600, px: 1, minWidth: 0 };

/**
 * Chip de una unidad vinculada. Con `onOpen` (MapPage resuelve la unidad) el
 * label abre la ficha (click / Enter, Tooltip «Abrir ficha»); la ✕ solo
 * desvincula (`stopPropagation`, no navega). Sin destino resoluble, como antes.
 */
function LinkedRecordChip({ rec, label, tooltip = null, onOpen = null, opening = false, onDelete, deleteLabel, testId }) {
  const canOpen = Boolean(onOpen && metaRecordOpenItem(rec));
  // Con el puntero sobre la ✕ se muestra solo su Tooltip («Desvincular»), no el del chip.
  const [chipTipOpen, setChipTipOpen] = useState(false);
  const [overDelete, setOverDelete] = useState(false);
  const handleDelete = onDelete ? (event) => {
    event?.stopPropagation?.();
    onDelete();
  } : undefined;
  const chip = (
    <Chip
      size="small"
      label={label}
      data-testid={testId}
      icon={<HomeWorkOutlinedIcon aria-hidden sx={metaLinkedRecordChipIconSx} />}
      sx={metaLinkedRecordChipSx(canOpen)}
      {...(canOpen ? {
        onClick: () => { if (!opening) onOpen(rec); },
        'aria-label': `Abrir ficha de ${describeMetaRecord(rec)}`,
        'aria-busy': opening || undefined,
      } : {})}
      onDelete={handleDelete}
      deleteIcon={(
        <Tooltip title={<span>Desvincular</span>} placement="top" arrow>
          <CloseIcon
            aria-label={deleteLabel}
            data-testid={testId ? `${testId}-unlink` : undefined}
            onMouseEnter={() => setOverDelete(true)}
            onMouseLeave={() => setOverDelete(false)}
          />
        </Tooltip>
      )}
    />
  );
  const lines = [canOpen ? 'Abrir ficha' : null, tooltip].filter(Boolean);
  if (lines.length === 0) return chip;
  // Tooltip de MUI y sin `title` nativo; el título va como nodo (con string + `describeChild`,
  // MUI vuelve a poner `title` en el chip cerrado).
  return (
    <Tooltip
      title={<span>{lines.join(' · ')}</span>}
      placement="top"
      arrow
      describeChild
      open={chipTipOpen && !overDelete}
      onOpen={() => setChipTipOpen(true)}
      onClose={() => setChipTipOpen(false)}
    >
      {chip}
    </Tooltip>
  );
}

export { LinkMenu };

/** Ayuda del botón principal de la campaña («Vincular a una unidad» = linkcampaignunit). */
export const META_LINK_CAMPAIGN_HELP = 'Vincular a una unidad. Vincula los anuncios que la campaña tiene hoy; si después se suma un anuncio, va a aparecer como sin vincular.';

/**
 * «+» Vincular a una unidad (campaña entera, `POST /linkcampaignunit`): mismo IconButton que el ⋮
 * (Historial: `listCardActionIconSx` + `recordCardHeaderIconSx`, hover teal) y la ayuda en el Tooltip.
 */
function LinkCampaignButton({ campaign, disabled, onClick }) {
  return (
    <Tooltip title={<span>{META_LINK_CAMPAIGN_HELP}</span>} placement="top" arrow>
      <span style={{ display: 'inline-flex' }}>
        <IconButton
          size="small"
          aria-label="Vincular a una unidad"
          disabled={disabled}
          onClick={onClick}
          sx={{ ...listCardActionIconSx, ...recordCardHeaderIconSx }}
          data-testid={`meta-campaign-${campaign.id}-link-unit`}
        >
          <AddIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </span>
    </Tooltip>
  );
}

const withCount = (label, n) => (n == null ? label : `${label} (${n})`);

/**
 * Empty state de la lista (29/10): mismo formato que la búsqueda sin resultados — centrado, título
 * en negrita, subtítulo corto y una acción de texto primaria. Lo usan la búsqueda, «Activas» en 0
 * y «Todas» en 0.
 */
function CampaignsEmptyState({ title, hint = null, actionLabel = null, onAction = null, testId, actionTestId }) {
  return (
    <Box sx={{ ...emptyStateBoxSx, minHeight: 0, py: 3 }} data-testid={testId} role="status">
      <Typography sx={emptyStateTitleSx}>{title}</Typography>
      {hint ? <Typography sx={emptyStateHintSx}>{hint}</Typography> : null}
      {actionLabel && onAction ? (
        <Button size="small" onClick={onAction} sx={leadsSummaryClearButtonSx} data-testid={actionTestId}>
          {actionLabel}
        </Button>
      ) : null}
    </Box>
  );
}

// Ajuste 12: miniatura de 56 px | contenido del anuncio (columna de texto alineada arriba).
const adWithThumbSx = { display: 'grid', gridTemplateColumns: '56px minmax(0, 1fr)', columnGap: 1.25, alignItems: 'start' };

// Vértice superior derecho («+» y ⋮): padding del bloque (12 px) + 2 px para centrar los íconos
// (18 px) con la primera línea de 22 px. La primera línea reserva 18 px por ícono + 4 entre ellos + 8.
const CORNER_ICON = 18;
const CORNER_GAP = 4;
const cornerReserve = (n) => n * CORNER_ICON + (n - 1) * CORNER_GAP + 8;
const adMenuCornerSx = { position: 'absolute', top: 14, right: 10, display: 'flex', alignItems: 'center', gap: `${CORNER_GAP}px`, zIndex: 1 };

// Anuncio con `is_active: false` dentro de una campaña activa: más tenue, legible y usable.
const inactiveAdSx = { opacity: 0.55 };

const AD_TONES = { unlinked: 'pending', unlinked_new: 'warning', linked: 'done', shared: 'done' };
const CAMPAIGN_TONES = { unlinked: 'pending', partial: 'partial', linked: 'done' };

/**
 * El único «Ver en Meta» de la vista: chico y secundario, al final del detalle
 * abierto de la campaña (la idea es quedarse en Terrio). Solo https (`readMetaLinks`).
 */
function CampaignMetaLink({ url, testId }) {
  if (!url) return null;
  return (
    <Link
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      underline="hover"
      data-testid={testId}
      sx={{ ...metaAdsMutedTextSx, fontSize: '12px', alignSelf: 'flex-end', display: 'inline-flex', alignItems: 'center', gap: 0.5, color: 'inherit' }}
    >
      Ver en Meta
      <OpenInNewIcon sx={{ fontSize: 12 }} aria-hidden />
    </Link>
  );
}

/**
 * «ARS 12.000 · 72 conversaciones + 3 formularios» (o «34 contactos» sin desglose;
 * plata en la moneda del objeto, nunca mezcla). El total de contactos va en el Tooltip.
 */
function metricsLine(metrics, currency) {
  if (!metrics) return '';
  const parts = [];
  if (metrics.spend != null) parts.push(formatMetaMoney(metrics.spend, currency));
  const contacts = formatMetaContacts(metrics);
  if (contacts) parts.push(contacts);
  return parts.join(' · ');
}

/** Gasto del período por moneda («ARS 114.150 · USD 120»), sin sumar monedas. */
function totalsLine(totals) {
  if (!totals) return '';
  if (totals.mixed) {
    return totals.byCurrency
      .filter((r) => r.metrics?.spend != null && r.currency)
      .map((r) => formatMetaMoney(r.metrics.spend, r.currency))
      .join(' · ');
  }
  return totals.total?.spend != null ? formatMetaMoney(totals.total.spend, totals.currency) : '';
}

/**
 * Un anuncio dentro de la campaña (mockup aprobado): bloque más suave que la tarjeta,
 * etiqueta «Anuncio» + punto, primero sus métricas (gasto · contactos), después las
 * píldoras de entrega / vínculo, el nombre de Meta más chico y la línea de métricas.
 *
 * `compact` (campaña con un solo anuncio): «1 anuncio · <nombre>», sin repetir las
 * métricas (son las de la campaña), con `primaryAction` («+» Vincular a una unidad, en el vértice) y
 * `extraMenuItems` (las de la campaña), que reemplazan a las del anuncio en su ⋮.
 *
 * Acciones: «Ver anuncio» es la miniatura (botón con Tooltip); a la vista solo «Sumarlo a las
 * mismas unidades» si es nuevo;
 * el vínculo por anuncio va al «⋯». `showUnits`: chips solo si su conjunto de unidades no
 * es el de la campaña (`metaCampaignUnitsPlacement`).
 */
function AdRow({
  ad, canLink, busy, onLink, onSuggest, onUnlinkRecord, onUnlinkAll, linkedByContext, onOpenRecord = null, openingKey = '',
  dimInactive = false, compact = false, primaryAction = null, extraMenuItems = null, onPreview = null, showUnits = true,
  writeActions = null,
}) {
  const metrics = metricsLine(ad.metrics, ad.currency);
  const adEmpty = metaPeriodIsEmpty(metaActivityValues(ad.metrics, ad.adsManager));
  const adActive = isMetaDeliveryActive(ad.delivery);
  // Activa a secas: solo el punto. Sin entrega reciente: punto verde + «Sin mostrarse hoy ni ayer».
  const adStatus = metaDeliveryShowsStatus(ad.delivery);
  const suggestion = ad.linkStatus === 'unlinked_new' && ad.suggestedRecords.length > 0;
  // Solo con `is_active === false` explícito (API vieja: null → sin cambios).
  const dimmed = dimInactive && ad.isActive === false;
  // Vinculado (verde): la unidad ya está en el chip con la ✕; la píldora solo para sin vincular / nuevo.
  // Con el «+» a la vista (bloque compacto) el «Sin vincular» sobra: el «+» ya dice que falta vincular.
  // «Anuncio nuevo sin vincular» se mantiene (avisa que es nuevo). Bloque compacto (1 anuncio = la
  // campaña): «Sin vincular» ya está en la cabecera de la campaña (reunión 29/10), no se repite.
  const plainUnlinked = !ad.linkedCount && ad.linkStatus !== 'unlinked_new';
  const linkPill = (ad.linkedCount > 0 && ad.linkStatus !== 'unlinked_new') || (plainUnlinked && (primaryAction || compact)) ? null : (
    <Box component="span" sx={getMetaAdsStatusPillSx(AD_TONES[ad.linkStatus] || 'pending')} data-testid={`meta-ad-${ad.id}-link`}>
      {metaAdLinkLabel(ad)}
    </Box>
  );
  // F2a: primero las acciones en Meta del anuncio (pausar / reactivar / renombrar), después el vínculo.
  const adItems = [...metaWriteItems(writeActions, 'ad', ad, adActive), ...(canLink ? [
    {
      key: 'link-ad',
      label: ad.linkedCount ? 'Sumar unidades a este anuncio' : 'Vincular solo este anuncio',
      onClick: () => onLink(ad),
    },
    ad.linkedCount > 0 ? { key: 'unlink-ad', label: 'Desvincular este anuncio de todas sus unidades', onClick: () => onUnlinkAll(ad) } : null,
  ] : [])];
  // Anuncio de una campaña con varios: solo lo del anuncio (lo de la campaña está en su ⋮).
  // Bloque compacto (1 anuncio, sin ⋮ de campaña): anuncio y campaña son lo mismo → solo lo de
  // la campaña (`extraMenuItems`), sin opciones duplicadas. Vacío → sin ⋮.
  const menuItems = (compact ? (extraMenuItems || []) : adItems).filter(Boolean);
  const showSuggest = canLink && suggestion;
  // ⋮ en el vértice superior derecho de la card (como Historial), centrado con la primera
  // línea (22 px); la primera línea reserva su ancho para que el texto no quede debajo.
  // El «+» (`primaryAction`) va a la izquierda del ⋮, en el mismo vértice.
  const hasMenu = menuItems.length > 0;
  const cornerCount = (primaryAction ? 1 : 0) + (hasMenu ? 1 : 0);
  const menu = cornerCount > 0 ? (
    <Box sx={adMenuCornerSx} data-testid={`meta-ad-${ad.id}-menu-corner`}>
      {primaryAction}
      {hasMenu ? <LinkMenu items={menuItems} disabled={busy} testId={`meta-ad-${ad.id}-menu`} /> : null}
    </Box>
  ) : null;
  const firstLineSx = cornerCount > 0 ? { pr: `${cornerReserve(cornerCount)}px` } : {};
  // «Ver anuncio» es la miniatura. Fila de acciones en la columna del contenido solo si hay
  // «Sumarlo a las mismas unidades» (si no, no se dibuja: sin huecos).
  const actionsRow = showSuggest ? (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }} data-testid={`meta-ad-${ad.id}-actions`}>
      {showSuggest ? (
        <Button size="small" variant="contained" disableElevation disabled={busy} onClick={() => onSuggest(ad)} sx={{ ...textButtonSx, px: 1.5, ...metaAdsContainedDisabledSx }}>
          Sumarlo a las mismas unidades
        </Button>
      ) : null}
    </Box>
  ) : null;
  return (
    <Box
      component={compact ? 'div' : 'li'}
      data-testid={`meta-ad-${ad.id}`}
      data-inactive={dimmed ? 'true' : undefined}
      data-compact={compact ? 'true' : undefined}
      aria-label={`Anuncio: ${ad.name}`}
      sx={{ ...metaAdsAdBlockSx, position: 'relative', ...(dimmed ? inactiveAdSx : {}) }}
    >
      {menu}
      {/* Ajuste 12: miniatura a la izquierda (abre «Ver anuncio»); las acciones, en la columna del contenido. */}
      <Box sx={adWithThumbSx}>
        <MetaAdThumb
          url={ad.thumbnailUrl}
          isVideo={ad.isVideo}
          onClick={onPreview ? () => onPreview(ad) : null}
          testId={`meta-ad-${ad.id}-thumb`}
        />
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0 }}>
          {compact ? (
            // Etiqueta | punto + nombre (envuelve) | píldora: todo centrado con la primera línea.
            <Box sx={{ ...metaAdsCompactHeadSx, ...firstLineSx }} data-testid={`meta-ad-${ad.id}-head`}>
              <Box component="span" sx={{ ...getMetaAdsLevelTagSx('ad'), ...metaAdsTagInLineSx }} data-testid={`meta-ad-${ad.id}-tag`}>1 anuncio</Box>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75, minWidth: 0 }}>
                <Box component="span" sx={metaAdsLineBoxSx}>
                  <MetaActiveDot delivery={ad.delivery} testId={`meta-ad-${ad.id}-dot`} />
                </Box>
                <Typography sx={{ ...metaAdsAdNameTextSx, color: 'rgba(255,255,255,0.78)', lineHeight: '22px', minWidth: 0 }} data-testid={`meta-ad-${ad.id}-name`}>
                  {ad.name}
                </Typography>
              </Box>
              {linkPill || <span />}
            </Box>
          ) : (
            <>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75, minWidth: 0, ...firstLineSx }}>
                <Box component="span" sx={{ ...getMetaAdsLevelTagSx('ad'), ...metaAdsTagInLineSx }} data-testid={`meta-ad-${ad.id}-tag`}>Anuncio</Box>
                <Box component="span" sx={metaAdsLineBoxSx}>
                  <MetaActiveDot delivery={ad.delivery} testId={`meta-ad-${ad.id}-dot`} />
                </Box>
                <MetaContactsTooltip note={metaContactsNote(ad.metrics)}>
                  <Typography sx={{ ...metaAdsAdMetricsTextSx, lineHeight: '22px', minWidth: 0 }} data-testid={`meta-ad-${ad.id}-metrics`}>
                    {(adEmpty ? '' : metrics) || 'Sin datos en el período'}
                  </Typography>
                </MetaContactsTooltip>
              </Box>
              {/* Paso 2: «Activa» es el punto verde (con Tooltip y aria-label); los demás estados, la píldora. */}
              {adStatus || linkPill ? (
                <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5 }}>
                  {adStatus ? <MetaDeliveryPill delivery={ad.delivery} label={ad.statusLabel} testId={`meta-ad-${ad.id}-delivery`} /> : null}
                  {linkPill}
                </Box>
              ) : null}
              <Typography sx={metaAdsAdNameTextSx} data-testid={`meta-ad-${ad.id}-name`}>{ad.name}</Typography>
              {/* Todo en 0 en el período: solo «Sin datos en el período» (sin «0 impresiones · CTR —…»). */}
              {adEmpty ? null : <MetaAdsManagerLine stats={ad.adsManager} currency={ad.currency} testId={`meta-ad-${ad.id}-ads-manager`} />}
            </>
          )}
          {showUnits && ad.linkedRecords.length > 0 ? (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }} data-testid={`meta-ad-${ad.id}-units`}>
              {ad.linkedRecords.map((rec) => {
                const text = `${describeMetaRecord(rec)}${rec.linkedVia === 'campaign' ? ' · por campaña' : ''}`;
                const by = describeMetaLinkedBy(rec, linkedByContext);
                return (
                  <LinkedRecordChip
                    key={metaRecordKey(rec)}
                    rec={rec}
                    label={text}
                    tooltip={by ? `${text} · ${by}` : text}
                    onOpen={onOpenRecord}
                    opening={openingKey === metaRecordKey(rec)}
                    onDelete={canLink && !busy ? () => onUnlinkRecord(ad, rec) : undefined}
                    deleteLabel={`Desvincular ${describeMetaRecord(rec)}`}
                    testId={`meta-ad-${ad.id}-record-${rec.id}`}
                  />
                );
              })}
            </Box>
          ) : null}
          {suggestion ? (
            <Typography sx={metaAdsMutedTextSx}>
              Lo sumaron después de vincular la campaña. Las otras unidades: {ad.suggestedRecords.map(describeMetaRecord).join(', ')}.
            </Typography>
          ) : null}
          {actionsRow}
        </Box>
      </Box>
    </Box>
  );
}

// Fade debajo de la cabecera pegada: se enciende pasado este margen (como `EDGE_THRESHOLD` de
// `useScrollEdgeFade`: no por un residuo de scroll).
const META_STICKY_FADE_THRESHOLD = 8;

/**
 * ¿La cabecera sticky de la tarjeta abierta está pegada (y el contenido ya pasa por debajo)?
 * IntersectionObserver sobre un centinela en el borde de arriba de la tarjeta, con la lista como
 * raíz: cuando la tarjeta subió más allá del aire de la cabecera + el margen, está pegada. Sin
 * IntersectionObserver (jsdom) queda en false. Solo cambia un atributo (sin cambios de alto).
 */
function useMetaStickyHeadStuck(enabled, scrollRoot) {
  const sentinelRef = useRef(null);
  const [stuck, setStuck] = useState(false);
  useEffect(() => {
    const el = sentinelRef.current;
    if (!enabled || !scrollRoot || !el || typeof IntersectionObserver === 'undefined') {
      setStuck(false);
      return undefined;
    }
    const io = new IntersectionObserver(([entry]) => {
      const rootTop = entry.rootBounds ? entry.rootBounds.top : -Infinity;
      setStuck(!entry.isIntersecting && entry.boundingClientRect.top < rootTop);
    }, { root: scrollRoot, rootMargin: `-${META_STICKY_HEAD_AIR + META_STICKY_FADE_THRESHOLD}px 0px 0px 0px`, threshold: 0 });
    io.observe(el);
    return () => io.disconnect();
  }, [enabled, scrollRoot]);
  return { sentinelRef, stuck: enabled && stuck };
}

/**
 * Campaña elegida en el buscador (reunión 29/10): siempre abierta, sin collapse. En el lugar de la
 * flecha va «Informe» (`CampaignReportButton`, con fase 1) y al lado «+» y ⋮ de la campaña. La
 * cabecera queda pegada arriba al scrollear el detalle.
 */
function CampaignCard({ campaign, canLink, actions, linkedByContext, adsManagerColumns = null, reachNote = null, scrollRoot = null }) {
  const { sentinelRef, stuck } = useMetaStickyHeadStuck(true, scrollRoot);
  const { onOpenRecord = null, openingKey = '' } = actions;
  const linkedUnits = campaignLinkedRecords(campaign);
  // La unidad una sola vez: en la campaña si todos los anuncios comparten el conjunto.
  const placement = metaCampaignUnitsPlacement(campaign);
  const adsWithUnits = new Set(placement.adIdsWithUnits);
  const busy = actions.isPending(`campaign:${campaign.id}`);
  // Paso 2 (fase 1): el gasto y los contactos van en «Resultados» del detalle (no se repiten en la
  // línea de la campaña). Sin fase 1 (`campaignDetail` null) la línea los muestra como antes.
  const detail = actions.campaignDetail || null;
  const campaignActive = isMetaDeliveryActive(campaign.delivery);
  const campaignStatus = metaDeliveryShowsStatus(campaign.delivery);
  const resultsGroup = detail ? metaCampaignResultsGroup(campaign.metrics, campaign.currency) : null;
  // Todo en 0 en el período (gasto, contactos, clics, impresiones): un solo texto, sin «ARS 0 · 0 contactos».
  const campaignEmpty = metaPeriodIsEmpty(metaActivityValues(campaign.metrics, campaign.adsManager));
  let metrics = resultsGroup ? '' : metricsLine(campaign.metrics, campaign.currency);
  if (campaignEmpty && !resultsGroup) metrics = 'Sin datos en el período';
  const contentId = `meta-campaign-${campaign.id}-ads`;
  // Un solo anuncio visible: versión compacta (no se repite la campaña). N = anuncios
  // visibles en el modo actual (Activas / Todas).
  const visibleAds = campaign.ads.length;
  const single = visibleAds === 1 ? campaign.ads[0] : null;
  const hiddenAds = Number(campaign.hiddenAdsCount) > 0 ? Number(campaign.hiddenAdsCount) : 0;
  // Con uno o varios anuncios a la vista, el bloque compacto o el subtítulo ya dicen cuántos.
  const adsPart = visibleAds === 0 ? plural(campaign.adsCount, 'anuncio', 'anuncios') : null;
  const campaignLine = [campaign.objectiveLabel, adsPart, metrics].filter(Boolean).join(' · ');
  const lineContactsNote = resultsGroup ? null : metaContactsNote(campaign.metrics);
  // Recomendaciones de Meta ocultas por decisión de producto (28/10/2026): se harán desde Terrio más adelante. Descomentar para reactivar.
  // Un solo chip por campaña: las suyas + las de sus anuncios, sin repetir.
  // const recommendations = useMemo(() => collectMetaRecommendations([
  //   { label: 'Campaña', recommendations: campaign.recommendations },
  //   ...campaign.ads.map((ad) => ({ label: `Anuncio «${ad.name}»`, recommendations: ad.recommendations })),
  // ]), [campaign]);
  const adRowProps = (ad) => ({
    ad,
    canLink,
    busy: busy || actions.isPending(`ad:${ad.id}`),
    onLink: actions.onLinkAd,
    onSuggest: actions.onSuggest,
    onUnlinkRecord: actions.onUnlinkAdRecord,
    onUnlinkAll: actions.onUnlinkAdAll,
    linkedByContext,
    onOpenRecord,
    openingKey,
    dimInactive: campaign.isActive !== false,
    onPreview: actions.onPreviewAd ? (ad) => actions.onPreviewAd(ad, campaign.name) : null,
    showUnits: adsWithUnits.has(ad.id),
    writeActions: actions.write || null,
  });
  // Principal: «Vincular a una unidad» si la campaña no tiene un conjunto común (con unidad, el chip).
  const linkButton = canLink && !placement.hasCommon ? (
    <LinkCampaignButton campaign={campaign} disabled={busy} onClick={() => actions.onLinkCampaign(campaign)} />
  ) : null;
  // Reunión 29/10: el chip de vínculo siempre a la vista («Sin vincular» / «N de M vinculados»).
  // Vinculada no lleva chip: la unidad ya está en su chip con la ✕.
  const showCampaignLinkPill = campaign.linkStatus !== 'linked';
  // F2a: primero las acciones en Meta de la campaña; con un solo anuncio van en el ⋮ del bloque compacto.
  const campaignMenuItems = [...metaWriteItems(actions.write, 'campaign', campaign, campaignActive, metaWriteBudgetOf('campaign', campaign)), ...(canLink ? [
    placement.hasCommon ? { key: 'link-campaign', label: 'Vincular la campaña a otra unidad', onClick: () => actions.onLinkCampaign(campaign) } : null,
    linkedUnits.length > 0 ? { key: 'unlink-campaign', label: 'Desvincular campaña', danger: true, onClick: () => actions.onUnlinkCampaignAll(campaign) } : null,
  ].filter(Boolean) : [])];
  const unitRows = placement.campaignRecords.map((rec) => (
    <LinkedUnitRow
      key={metaRecordKey(rec)}
      rec={rec}
      adsTotal={visibleAds}
      onOpen={onOpenRecord}
      opening={openingKey === metaRecordKey(rec)}
      onDelete={canLink && !busy ? () => actions.onUnlinkCampaignRecord(campaign, rec) : undefined}
      deleteLabel={`Quitar ${describeMetaRecord(rec)} de la campaña`}
      testId={`meta-campaign-${campaign.id}-record-${rec.id}`}
    />
  ));
  // «Unidades vinculadas» (t994u): una fila por unidad (antes, chips). «Informe», «+» y ⋮ de la
  // campaña van en la cabecera; con un solo anuncio, «+» y ⋮ en el vértice del bloque compacto.
  // Sin unidades, no hay sección (el chip «Sin vincular» de la cabecera ya lo dice).
  const showUnitsRow = unitRows.length > 0;
  const hasCampaignMenu = campaignMenuItems.length > 0;
  // Siempre solo el ícono (t1053u): «Informe de la unidad» va en el Tooltip y el aria-label.
  const showCornerLinkMenu = !single && (linkButton || hasCampaignMenu);
  const reportAtEdge = !showCornerLinkMenu;
  const reportButton = detail ? <CampaignReportButton campaign={campaign} detail={detail} edge={reportAtEdge} /> : null;
  const campaignMenu = reportButton || showCornerLinkMenu ? (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: `${CORNER_GAP}px`, flexShrink: 0 }} data-testid={`meta-campaign-${campaign.id}-menu-corner`}>
      {reportButton}
      {showCornerLinkMenu ? linkButton : null}
      {showCornerLinkMenu && hasCampaignMenu ? <LinkMenu items={campaignMenuItems} disabled={busy} testId={`meta-campaign-${campaign.id}-menu`} /> : null}
    </Box>
  ) : null;
  const unitsRow = showUnitsRow ? (
    <Box component="section" aria-labelledby={`${contentId}-units-title`} data-testid={`meta-campaign-${campaign.id}-units`}>
      <Typography component="h4" id={`${contentId}-units-title`} sx={metaLinkedUnitsTitleSx}>
        {unitRows.length === 1 ? 'Unidad vinculada' : 'Unidades vinculadas'}
      </Typography>
      <Box component="ul" sx={metaLinkedUnitsListSx}>{unitRows}</Box>
    </Box>
  ) : null;
  const resultsContent = (
    <>
      {resultsGroup && campaignEmpty ? (
        // Detalle con todo en 0: un solo mensaje en vez de Resultados / Alcance / Clics / Costos en 0.
        <Typography sx={{ ...metaAdsMutedTextSx, py: 0.5 }} data-testid={`meta-campaign-${campaign.id}-results-empty`}>
          {META_NO_DATA_IN_PERIOD}
        </Typography>
      ) : (
        <MetaAdsManagerGrid
          stats={campaign.adsManager}
          currency={campaign.currency}
          columns={adsManagerColumns}
          reachNote={reachNote}
          testId={`meta-campaign-${campaign.id}-ads-manager`}
          leadGroups={resultsGroup ? [resultsGroup] : []}
          hideMissingReach={Boolean(detail)}
        />
      )}
      {unitsRow}
      {visibleAds === 0 ? (
        <Typography sx={metaAdsMutedTextSx}>Esta campaña no tiene anuncios sincronizados todavía.</Typography>
      ) : single ? (
        <AdRow {...adRowProps(single)} compact primaryAction={linkButton} extraMenuItems={campaignMenuItems} />
      ) : (
        <>
          <Typography component="h4" sx={metaAdsSectionTitleSx} id={`${contentId}-title`} data-testid={`meta-campaign-${campaign.id}-ads-title`}>
            {`Anuncios de esta campaña (${visibleAds})`}
            {hiddenAds > 0 ? (
              <Box component="span" sx={{ fontWeight: 400, color: 'rgba(255,255,255,0.5)' }}>
                {` · ${plural(hiddenAds, 'oculto', 'ocultos')}`}
              </Box>
            ) : null}
          </Typography>
          <Box component="ul" sx={metaAdsAdListSx} aria-labelledby={`${contentId}-title`}>
            {campaign.ads.map((ad) => <AdRow key={ad.id} {...adRowProps(ad)} />)}
          </Box>
        </>
      )}
      <CampaignMetaLink url={campaign.metaUrl} testId={`meta-campaign-${campaign.id}-meta-link`} />
    </>
  );
  // Recomendaciones de Meta ocultas por decisión de producto (28/10/2026): se harán desde Terrio más adelante. Descomentar para reactivar. (sumaba `|| recommendations.count > 0`)
  const hasStatusChips = campaignStatus || showCampaignLinkPill || campaign.adsUnlinkedNewCount > 0;
  // Chips de estado (entrega, vínculo, nuevos): en la cabecera, a la izquierda de «Informe» / ⋮ (t990u).
  const statusChips = hasStatusChips ? (
    <Box sx={metaCampaignHeadChipsSx} data-testid={`meta-campaign-${campaign.id}-chips`}>
      {campaignStatus ? (
        <MetaDeliveryPill delivery={campaign.delivery} label={campaign.statusLabel} testId={`meta-campaign-${campaign.id}-delivery`} />
      ) : null}
      {showCampaignLinkPill ? (
        <Box component="span" sx={getMetaAdsStatusPillSx(CAMPAIGN_TONES[campaign.linkStatus] || 'pending')} data-testid={`meta-campaign-${campaign.id}-link`}>
          {metaCampaignLinkLabel(campaign)}
        </Box>
      ) : null}
      {campaign.adsUnlinkedNewCount > 0 ? (
        <Box component="span" sx={getMetaAdsStatusPillSx('warning')} data-testid={`meta-campaign-${campaign.id}-new`}>
          {plural(campaign.adsUnlinkedNewCount, 'nuevo', 'nuevos')}
        </Box>
      ) : null}
      {/* Recomendaciones de Meta ocultas por decisión de producto (28/10/2026): se harán desde Terrio más adelante. Descomentar para reactivar.
      <MetaRecommendations summary={recommendations} title={campaign.name} testId={`meta-campaign-${campaign.id}-recommendations`} />
      */}
    </Box>
  ) : null;
  return (
    <Box
      // El fade de la cabecera sticky no se sale de la tarjeta al llegar a su final (clip-path no
      // crea un scroller: el sticky sigue pegándose a la lista).
      sx={{ ...metaAdsCardSx, gap: 1, position: 'relative', clipPath: 'inset(0 round 12px)' }}
      data-testid={`meta-campaign-${campaign.id}`}
      aria-label={`Campaña: ${campaign.name}`}
      role="region"
    >
      {/* Centinela del sticky (absoluto: no suma gap). */}
      <Box ref={sentinelRef} aria-hidden sx={{ position: 'absolute', top: 0, left: 0, width: 1, height: '1px', pointerEvents: 'none' }} />
      {/* Cabecera (t1052u): una línea fija, sticky dentro de su tarjeta. */}
      <Box
        sx={{ ...metaCampaignHeadContainerSx, ...metaCampaignStickyHeadSx }}
        data-testid={`meta-campaign-${campaign.id}-head`}
        data-sticky="true"
        data-stuck={stuck ? 'true' : undefined}
      >
        {/* t1052u: sin nombre ni punto (ya están en el buscador, que elige una sola campaña). Una
            línea de alto fijo: objetivo + chips a la izquierda; «Informe de la unidad», «+» y ⋮ a la derecha. */}
        <Box sx={metaCampaignHeadLineSx}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0, flex: 1 }}>
            {campaignLine ? (
              <MetaContactsTooltip note={lineContactsNote}>
                {/* Completo en el title solo sin Tooltip de contactos (no se pisan). */}
                <Typography noWrap title={lineContactsNote ? undefined : campaignLine} sx={{ ...metaAdsMutedTextSx, minWidth: 0 }} data-testid={`meta-campaign-${campaign.id}-metrics`}>
                  {campaignLine}
                </Typography>
              </MetaContactsTooltip>
            ) : null}
            {statusChips}
          </Box>
          {campaignMenu}
        </Box>
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }} data-testid={`meta-campaign-${campaign.id}-detail`}>
        {detail ? <MetaCampaignDetail campaign={campaign} detail={detail} results={resultsContent} write={actions.write} /> : resultsContent}
      </Box>
    </Box>
  );
}

/**
 * Carga de Campañas (ajuste 29/10, t988u): sin la pila de tarjetas grises (no se parecía a lo que
 * venía). Mientras carga, el buscador queda en gris con un loader; una línea de skeleton en el lugar
 * del gasto total (a la derecha del período) y en la lista el estado inicial («Elegí una campaña») o, si llega una campaña
 * preseleccionada (desde la ficha), el skeleton de UNA tarjeta abierta.
 */
export function MetaCampaignsTotalsSkeleton() {
  return (
    // Mismo alto que la fila del gasto (28) y un ancho parecido al de «Gasto total ARS 0.000.000».
    <Box sx={{ display: 'flex', alignItems: 'center', minHeight: 28 }} data-testid="meta-campaigns-totals-skeleton" aria-hidden="true">
      <Skeleton variant="rounded" height={14} width={150} sx={metaAdsSkeletonMutedSx} />
    </Box>
  );
}

/**
 * Lugar de la línea de métricas de la cuenta («N impresiones · CTR · CPC · CPM ⓘ», t998u): una línea
 * (18 px, el alto del texto) reservada en carga y con el dato, así nada se mueve al llegar. Con monedas
 * mezcladas crece (una línea por moneda); sin línea (período vacío), queda el hueco de una línea.
 */
export const META_CAMPAIGNS_METRICS_LINE_PX = 18;
function MetaCampaignsMetricsSlot({ children }) {
  return (
    <Box sx={{ minHeight: META_CAMPAIGNS_METRICS_LINE_PX, display: 'flex', flexDirection: 'column', justifyContent: 'center', flexShrink: 0 }} data-testid="meta-campaigns-metrics-slot">
      {children}
    </Box>
  );
}

/** Skeleton de la línea de métricas: una línea de ~12 px y 60 % de ancho (como el del gasto total). */
export function MetaCampaignsMetricsSkeleton() {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', height: META_CAMPAIGNS_METRICS_LINE_PX }} data-testid="meta-campaigns-metrics-skeleton" aria-hidden="true">
      <Skeleton variant="rounded" height={12} sx={{ ...metaAdsSkeletonMutedSx, width: '60%' }} />
    </Box>
  );
}

/** Skeleton de la campaña que viene (una sola tarjeta, con la forma de la abierta). */
export function MetaCampaignCardSkeleton() {
  return (
    <Box sx={{ ...metaAdsCardSx, gap: 1, flexShrink: 0 }} data-testid="meta-campaign-skeleton-card" aria-busy="true">
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, height: 22 }}>
        <Skeleton variant="circular" width={8} height={8} sx={{ ...metaAdsSkeletonSx, flexShrink: 0 }} />
        <Skeleton variant="rounded" height={16} sx={{ ...metaAdsSkeletonSx, width: '62%' }} />
        <Skeleton variant="rounded" width={18} height={18} sx={{ ...metaAdsSkeletonMutedSx, ml: 'auto', flexShrink: 0 }} />
      </Box>
      <Skeleton variant="rounded" height={12} sx={{ ...metaAdsSkeletonMutedSx, width: '74%' }} />
      <Skeleton variant="rounded" height={28} sx={{ ...metaAdsSkeletonMutedSx, width: '100%', borderRadius: '8px' }} />
      {[0, 1, 2].map((i) => (
        <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
          <Skeleton variant="rounded" height={12} sx={{ ...metaAdsSkeletonMutedSx, width: `${40 - i * 6}%` }} />
          <Skeleton variant="rounded" height={12} sx={{ ...metaAdsSkeletonMutedSx, width: '22%' }} />
        </Box>
      ))}
    </Box>
  );
}

/** ⓘ del gasto total (va arriba de la definición de contactos). */
export const META_TOTALS_HELP = 'Suma todas las campañas de la cuenta en el período elegido, incluidas las que el filtro no muestra.';

/** Estado inicial de Campañas (reunión 29/10): no hay campaña elegida en el buscador. */
export const META_PICK_CAMPAIGN_TITLE = 'Elegí una campaña';
export const META_PICK_CAMPAIGN_HINT = 'Buscala por nombre o abrí la lista para ver su detalle.';

/**
 * Vista «Campañas» del panel Meta Ads (E3; reunión 29/10): buscador con desplegable (filtro
 * «Activas · Todas» adentro) y el detalle de UNA campaña elegida, siempre abierto. Con `canLink`
 * (`viewer_can_link`) vincula / desvincula por anuncio y por campaña; sin él, solo lectura.
 *
 * La elección vive en el panel (`selectedCampaignId` / `onSelectCampaign`: dura mientras el panel
 * está abierto); sin esas props, acá. `focusCampaignId` (ficha → «Ir a Campañas»): la elige al
 * llegar la lista, pasando a «Todas» si no está en «Activas»; después llama `onFocusHandled`.
 *
 * `viewerUserId` (sub del que mira) solo sirve para el tooltip «Vinculó: Vos / Dueño / Un editor».
 *
 * @param {{ canLink: boolean, canManage: boolean, ownerUserId?: string|null, viewerUserId?: string|null,
 *   campaignsState: object, linkActions: object, includeInactive: boolean,
 *   onIncludeInactiveChange: (next: boolean) => void, connecting: boolean, onReconnect: () => void,
 *   selectedCampaignId?: string|null, onSelectCampaign?: (id: string|null) => void,
 *   focusCampaignId?: string|null, onFocusHandled?: () => void }} props
 */
export default function MetaCampaignsView({
  canLink,
  canManage,
  ownerUserId = null,
  viewerUserId = null,
  campaignsState,
  linkActions,
  includeInactive,
  onIncludeInactiveChange,
  connecting,
  onReconnect,
  onOpenRecord = null,
  periodPreset = META_DEFAULT_PERIOD_PRESET,
  onPeriodPresetChange = null,
  lastSyncAt = null,
  // Header fijo: a la derecha de la frescura («Análisis», «Informe»). ↻ y ⚙ van en el header del panel.
  headerTools = null,
  // Frescura cuando la API no manda `data_freshness_text` («Datos al día · Actualizado…» del banner).
  fallbackFreshness = '',
  // Al inicio de la lista (dentro del scroll): banner de carga / pausa / error y avisos.
  listNotices = null,
  // Fase 1: datos del detalle de la campaña (período, dueño…); null = sin pestañas ni «Informe».
  campaignDetail = null,
  // F2a: acciones en Meta `{ isOwner, onApplied(result, target), onOpenSettings(), onReconnect(), onStale() }`;
  // null (sin fase 1 o sin el bloque `write`) = el ⋮ queda como antes.
  writeActions = null,
  selectedCampaignId: selectedProp,
  onSelectCampaign: onSelectProp,
  focusCampaignId = null,
  onFocusHandled = null,
}) {
  // Scroll solo en la lista (regla ui-scroll-edge-fade: el fade va en el nodo que scrollea).
  const { scrollRef: listScrollRef, fadeTop: listFadeTop, fadeBottom: listFadeBottom } = useScrollEdgeFade();
  // El nodo de la lista también es la raíz del IntersectionObserver de la cabecera sticky.
  const [listNode, setListNode] = useState(null);
  const listRef = useCallback((node) => { listScrollRef(node); setListNode(node); }, [listScrollRef]);
  const {
    loaded, loading, error, campaigns, totals, period, reload, hiddenCampaignsCount = 0,
    activeCampaignsCount = null, totalCampaignsCount = null, hasCampaignCounts = null,
    adsManagerColumns = null, adsManagerReachNote = null, totalsAdsManager = null, totalsAdsManagerByCurrency = [],
    dataStale = false, dataFreshnessText = null, loadedIncludeInactive = null,
  } = campaignsState;
  // Campaña elegida: controlada por el panel o, sin props, acá.
  const [ownSelected, setOwnSelected] = useState(null);
  const controlled = onSelectProp !== undefined;
  const selectedId = controlled ? (selectedProp ?? null) : ownSelected;
  const selectCampaign = useCallback((id) => {
    const next = id == null ? null : String(id);
    if (controlled) onSelectProp(next);
    else setOwnSelected(next);
  }, [controlled, onSelectProp]);
  const [dialog, setDialog] = useState(null);
  // «Ver anuncio» (ajuste 12): el anuncio abierto; la URL de Meta vive solo dentro del diálogo.
  const [preview, setPreview] = useState(null);
  // F2a: la acción en Meta abierta (`{ level, action, id, name, campaignId }`).
  const [writeTarget, setWriteTarget] = useState(null);
  const linkedByContext = useMemo(() => ({ viewerUserId, ownerUserId }), [viewerUserId, ownerUserId]);

  // La lista que está en pantalla es la del filtro actual (no la anterior mientras llega la nueva).
  const listReady = loaded && !loading && (loadedIncludeInactive == null || loadedIncludeInactive === includeInactive);
  const has = (id) => (campaigns || []).some((c) => String(c.id) === String(id));
  // Autoselección con una sola campaña: una vez por lista (si la ✕ la quita, no vuelve sola).
  const autoKeyRef = useRef('');
  useEffect(() => {
    if (!listReady) return;
    if (focusCampaignId) {
      // Ficha → «Ir a Campañas»: la elige; si no está en «Activas», prueba en «Todas».
      if (has(focusCampaignId)) {
        selectCampaign(focusCampaignId);
        onFocusHandled?.();
      } else if (!includeInactive) {
        onIncludeInactiveChange(true);
      } else {
        onFocusHandled?.();
      }
      return;
    }
    const keep = Boolean(selectedId) && has(selectedId);
    // La elegida ya no está (cambio de filtro, pausada en «Activas», eliminada): vuelve el mensaje inicial.
    if (selectedId && !keep) selectCampaign(null);
    const key = `${includeInactive ? 'all' : 'active'}:${(campaigns || []).map((c) => c.id).join(',')}`;
    if (autoKeyRef.current !== key) {
      autoKeyRef.current = key;
      if (!keep && campaigns.length === 1) selectCampaign(campaigns[0].id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listReady, campaigns, focusCampaignId, includeInactive, selectedId]);

  const confirmUnlink = (title, description) => confirmDialog({
    title, description, confirmLabel: 'Desvincular', variant: 'danger',
  }).then((r) => Boolean(r?.isConfirmed));

  // Chip de unidad → ficha (MapPage vía MetaAdsContext). `user_lot` pide el `lot_id`
  // (getuserlotdetail) y la parcela CAD el proyecto, igual que los intereses de un lead.
  const { openItem, openingItemId } = useLeadItemOpener({ ownerUserId, onOpenTarget: onOpenRecord || undefined });
  const openRecord = onOpenRecord ? (rec) => {
    const item = metaRecordOpenItem(rec);
    if (item) openItem(item);
  } : null;

  const actions = {
    campaignDetail,
    write: writeActions ? { ...writeActions, onAction: setWriteTarget } : null,
    onOpenRecord: openRecord,
    openingKey: openingItemId,
    isPending: linkActions.isPending,
    onPreviewAd: (ad, campaignName) => setPreview({ ad, campaignName }),
    onLinkAd: (ad) => setDialog({ kind: 'ad', target: ad, name: ad.name, linkedRecords: ad.linkedRecords }),
    onLinkCampaign: (campaign) => setDialog({
      kind: 'campaign', target: campaign, name: campaign.name, linkedRecords: campaignLinkedRecords(campaign),
    }),
    onSuggest: (ad) => linkActions.linkAd(ad, ad.suggestedRecords),
    onUnlinkAdRecord: (ad, rec) => linkActions.unlinkAd(ad, [rec]),
    onUnlinkAdAll: async (ad) => {
      const ok = await confirmUnlink(
        '¿Desvincular el anuncio de todas sus unidades?',
        `«${ad.name}» deja de contar para ${plural(ad.linkedCount, 'su unidad', `sus ${ad.linkedCount} unidades`)}. No se borran métricas; lo podés volver a vincular.`,
      );
      if (ok) linkActions.unlinkAd(ad, null);
    },
    onUnlinkCampaignRecord: async (campaign, rec) => {
      const ok = await confirmUnlink(
        `¿Quitar ${describeMetaRecord(rec)} de toda la campaña?`,
        `Se desvincula de ${plural(rec.adsCount, 'anuncio', 'anuncios')} de «${campaign.name}».`,
      );
      if (ok) linkActions.unlinkCampaign(campaign, [rec]);
    },
    onUnlinkCampaignAll: async (campaign) => {
      const ok = await confirmUnlink(
        '¿Desvincular toda la campaña?',
        `Se borran todos los vínculos de los anuncios de «${campaign.name}» con sus unidades (los hechos por anuncio y por campaña). No se borran métricas.`,
      );
      if (ok) linkActions.unlinkCampaign(campaign, null);
    },
  };

  const handleDialogSubmit = async (records) => {
    const current = dialog;
    const res = current.kind === 'campaign'
      ? await linkActions.linkCampaign(current.target, records, { silentError: true })
      : await linkActions.linkAd(current.target, records, { silentError: true });
    if (res?._httpOk) setDialog(null);
    return res;
  };

  // Ajuste 9: «Activas (N) · Todas (M)» con los conteos de la API (no dependen del filtro).
  // API vieja (sin conteos): mismas opciones sin números. «Todas» = include_inactive=1.
  // «Todas (M)» sin las eliminadas; si todavía no se sabe cuántas hay (vista «Activas»), sin número.
  // Reunión 29/10: el filtro va adentro del desplegable del buscador.
  const hasCounts = hasCampaignCounts ?? (activeCampaignsCount != null && totalCampaignsCount != null);

  // Ajuste 10: período como el Administrador de anuncios. Queda visible mientras recarga.
  // Ajuste 11: con `data_freshness_text` de la API se muestra ese (arriba de la lista) y no
  // el «Datos hasta las HH:MM» armado acá; API vieja / null → lo de antes.
  const dataUntil = !dataFreshnessText && periodPreset === 'today' ? formatMetaDataUntil(lastSyncAt) : '';
  const freshness = dataFreshnessText ? (
    <Typography
      sx={{ ...metaAdsMutedTextSx, fontSize: '12px', ...(dataStale ? { color: 'rgba(240, 200, 140, 0.85)' } : {}) }}
      data-testid="meta-campaigns-freshness"
      data-stale={dataStale ? 'true' : undefined}
    >
      {dataFreshnessText}
    </Typography>
  ) : null;
  const periodSelect = onPeriodPresetChange ? (
    <MetaPeriodSelect value={periodPreset} onChange={onPeriodPresetChange} testId="meta-campaigns-period-select" />
  ) : null;
  const hiddenLegacy = loaded && !hasCounts && !includeInactive && hiddenCampaignsCount > 0;
  const spend = totalsLine(totals);
  // Todo en 0 en todas las monedas (gasto, contactos e impresiones): una sola línea, sin ⓘ.
  const totalsEmpty = metaPeriodIsEmpty([
    totals?.total?.spend, totals?.total?.contacts,
    ...(totals?.byCurrency || []).flatMap((r) => [r.metrics?.spend, r.metrics?.contacts]),
    totalsAdsManager?.impressions,
    ...totalsAdsManagerByCurrency.map((r) => r.stats?.impressions),
  ]);
  // «Gasto total ARS X ⓘ» en la fila del período, a la derecha (t989u): etiqueta gris, monto
  // destacado; si no entra baja debajo del selector (wrap) sin cortar el monto. Cargando: una
  // línea de skeleton en ese lugar. Sin datos en el período / sin lista: nada (lo dice la lista).
  let spendSlot = null;
  if (!loaded) {
    spendSlot = <MetaCampaignsTotalsSkeleton />;
  } else if (spend && !totalsEmpty && !(error && campaigns.length === 0)) {
    spendSlot = (
      <Box sx={metaCampaignsSpendSx} data-testid="meta-campaigns-totals-row">
        <Typography component="span" sx={metaCampaignsSpendTextSx} data-testid="meta-campaigns-totals">
          <Box component="span" sx={metaCampaignsSpendLabelSx}>
            {`Gasto total${metaPeriodIsPartial(period) ? ' (hoy, parcial)' : ''}`}
          </Box>
          {' '}
          <Box component="span" sx={metaCampaignsSpendAmountSx} data-testid="meta-campaigns-totals-amount">{spend}</Box>
        </Typography>
        <MetaContactsHelp
          testId="meta-campaigns-totals-help"
          ariaLabel="Qué suma el gasto total"
          note={META_TOTALS_HELP}
        />
      </Box>
    );
  }
  // Sin campañas en «Activas» (o en «Todas»): el empty state de la lista lo dice; el buscador, en gris.
  const noCampaigns = listReady && !error && campaigns.length === 0;
  // Encabezado fijo (fuera del scroll): frescura, buscador y período.
  const header = (
    <Box
      sx={metaCampaignsHeaderSx}
      data-testid="meta-campaigns-header"
      role="toolbar"
      aria-label="Filtros de campañas"
    >
      {/* Frescura + herramientas: fila de alto fijo (no cambia al cargar ni al scrollear). */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minHeight: 28 }} data-testid="meta-campaigns-freshness-row">
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {freshness}
          {dataUntil ? (
            <Typography sx={{ ...metaAdsMutedTextSx, fontSize: '12px' }} data-testid="meta-campaigns-data-until">{dataUntil}</Typography>
          ) : null}
          {!freshness && !dataUntil && fallbackFreshness ? (
            <Typography sx={{ ...metaAdsMutedTextSx, fontSize: '12px' }} data-testid="meta-campaigns-sync-freshness">{fallbackFreshness}</Typography>
          ) : null}
        </Box>
        {headerTools}
      </Box>
      <MetaCampaignPicker
        campaigns={campaigns}
        selectedId={selectedId}
        onSelect={selectCampaign}
        includeInactive={includeInactive}
        onIncludeInactiveChange={onIncludeInactiveChange}
        activeLabel={withCount('Activas', hasCounts ? activeCampaignsCount : null)}
        allLabel={withCount('Todas', hasCounts ? totalCampaignsCount : null)}
        loading={!listReady}
        disabled={noCampaigns}
        placeholder={noCampaigns ? (includeInactive ? 'No hay campañas' : 'No hay campañas activas') : undefined}
      />
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }} data-testid="meta-campaigns-period">
        {periodSelect}
        {/* API vieja (sin conteos para el filtro): «N ocultas» como antes. */}
        {hiddenLegacy ? (
          <Typography sx={metaAdsMutedTextSx} data-testid="meta-campaigns-hidden">
            {plural(hiddenCampaignsCount, 'oculta', 'ocultas')}
          </Typography>
        ) : null}
        {spendSlot ? <Box sx={{ ml: 'auto', minWidth: 0, display: 'flex', justifyContent: 'flex-end' }}>{spendSlot}</Box> : null}
      </Box>
    </Box>
  );

  // Header siempre igual; skeleton, error y detalle van dentro del área que scrollea.
  const layout = (content) => (
    <Box sx={metaCampaignsLayoutSx} data-testid="meta-campaigns-layout">
      {header}
      <Box
        ref={listRef}
        sx={{ ...metaCampaignsListSx, ...getScrollEdgeFadeSx(listFadeTop, listFadeBottom, selectedId ? META_STICKY_FADE_SIZE : undefined) }}
        data-testid="meta-campaigns-list"
        aria-busy={!loaded || loading || undefined}
      >
        {listNotices}
        {content}
      </Box>
    </Box>
  );

  const pickEmpty = (
    <PanelEmptyState
      icon={CampaignOutlinedIcon}
      title={META_PICK_CAMPAIGN_TITLE}
      hint={META_PICK_CAMPAIGN_HINT}
      testId="meta-campaigns-pick"
      sx={{ minHeight: 0, py: 4 }}
    />
  );
  // Primera carga: sin datos todavía. Una línea en el lugar del gasto y debajo lo que va a venir:
  // la campaña pedida desde la ficha (skeleton de una tarjeta) o «Elegí una campaña».
  // Recarga (↻, período, filtro) con datos: la vista queda como está; el loader va en el buscador.
  if (!loaded) {
    return layout(
      <>
        {/* t998u: el lugar de la línea de métricas ya reservado (no empuja «Elegí una campaña» al llegar). */}
        <MetaCampaignsMetricsSlot><MetaCampaignsMetricsSkeleton /></MetaCampaignsMetricsSlot>
        {focusCampaignId || selectedId ? <MetaCampaignCardSkeleton /> : pickEmpty}
      </>,
    );
  }

  if (error && campaigns.length === 0) {
    return layout(
      <PanelEmptyState
        icon={CampaignOutlinedIcon}
        title="No pudimos cargar las campañas"
        hint={error}
        actionLabel="Reintentar"
        actionIcon={RefreshIcon}
        onAction={() => reload()}
        testId="meta-campaigns-error"
      />,
    );
  }

  const selected = selectedId ? campaigns.find((c) => String(c.id) === String(selectedId)) || null : null;

  return layout(
    <>
      {linkActions.reconnectError && canManage ? (
        <InfoNotice
          title="Hay que volver a conectar Meta"
          description={linkActions.reconnectError}
          actionLabel="Reconectar"
          onAction={onReconnect}
          actionDisabled={connecting}
          data-testid="meta-campaigns-reconnect"
        />
      ) : null}
      {!canLink ? (
        <InfoNotice
          title="Solo lectura"
          description="Podés ver las campañas y sus vínculos, pero no cambiarlos."
          data-testid="meta-campaigns-read-only"
        />
      ) : null}
      {/* Lista vacía: el empty state ya lo dice (un solo mensaje, sin «Todavía no hay datos…»).
          t998u: siempre dentro del mismo lugar de una línea que el skeleton de carga (sin salto). */}
      <MetaCampaignsMetricsSlot>
      {totalsEmpty && campaigns.length === 0 ? null : totalsEmpty ? (
        <Typography sx={metaAdsMutedTextSx} data-testid="meta-campaigns-totals-empty">{META_NO_DATA_IN_PERIOD}</Typography>
      ) : (
        <>
          {/* El gasto total va arriba, en la fila del período; acá queda la línea de métricas. */}
          {/* Ajuste 10: con monedas mezcladas CPC/CPM del total vienen null → una línea por moneda. */}
          {totals?.mixed && totalsAdsManagerByCurrency.length > 0 ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }} data-testid="meta-campaigns-totals-ads-manager">
              {totalsAdsManagerByCurrency.map((row, i) => (
                <MetaAdsManagerLine
                  key={row.currency}
                  stats={row.stats}
                  currency={row.currency}
                  prefix={row.currency}
                  testId={`meta-campaigns-totals-ads-manager-${row.currency}`}
                  // Una línea por moneda: el ⓘ una sola vez (en la última).
                  showHelp={i === totalsAdsManagerByCurrency.length - 1}
                />
              ))}
            </Box>
          ) : (
            <MetaAdsManagerLine
              stats={totalsAdsManager}
              currency={totals?.currency || null}
              testId="meta-campaigns-totals-ads-manager"
            />
          )}
        </>
      )}
      </MetaCampaignsMetricsSlot>
      {campaigns.length === 0 ? (
        includeInactive ? (
          <CampaignsEmptyState title="No hay campañas en las cuentas que seguís" testId="meta-campaigns-empty" />
        ) : (
          <CampaignsEmptyState
            title="No hay campañas activas"
            hint={totalCampaignsCount !== 0 ? 'Las pausadas y terminadas están en Todas.' : null}
            actionLabel={totalCampaignsCount !== 0 ? withCount('Ver todas', totalCampaignsCount) : null}
            onAction={() => onIncludeInactiveChange(true)}
            testId="meta-campaigns-empty"
            actionTestId="meta-campaigns-empty-view-all"
          />
        )
      ) : selected ? (
        <CampaignCard
          key={selected.id}
          campaign={selected}
          canLink={canLink}
          actions={actions}
          linkedByContext={linkedByContext}
          adsManagerColumns={adsManagerColumns}
          reachNote={adsManagerReachNote}
          scrollRoot={listNode}
        />
      ) : focusCampaignId ? (
        // Pedida desde la ficha y todavía buscándola (p. ej. pasando a «Todas»): la tarjeta que viene.
        <MetaCampaignCardSkeleton />
      ) : pickEmpty}
      <MetaLinkUnitsDialog
        target={dialog}
        ownerUserId={ownerUserId}
        onClose={() => setDialog(null)}
        onSubmit={handleDialogSubmit}
      />
      {writeActions && writeTarget ? (
        <MetaWriteDialog
          target={writeTarget}
          onClose={() => setWriteTarget(null)}
          onApplied={(result, target) => { setWriteTarget(null); writeActions.onApplied(result, target); }}
          onOpenSettings={writeActions.onOpenSettings ? () => { setWriteTarget(null); writeActions.onOpenSettings(); } : null}
          onReconnect={writeActions.onReconnect ? () => { setWriteTarget(null); writeActions.onReconnect(); } : null}
          onStale={writeActions.onStale || null}
        />
      ) : null}
      {preview ? (
        <MetaAdPreviewDialog
          key={preview.ad.id}
          ad={preview.ad}
          campaignName={preview.campaignName}
          ownerUserId={ownerUserId}
          onClose={() => setPreview(null)}
        />
      ) : null}
    </>,
  );
}
